/**
 * Marketing attribution capture for the book-a-demo funnel.
 *
 * Reads UTM / fbclid / referrer / landing_page from the URL on every page
 * load, persists a first-touch snapshot in localStorage, and exposes the
 * snapshot to the demo intake form so it can be forwarded to Pipedrive.
 *
 * Attribution model: first-touch with refresh-on-new-paid-source.
 *   - First visit (any source, even organic) stamps landing_page + referrer.
 *   - First visit carrying a utm_source stamps the full UTM block.
 *   - A later visit with a *different* utm_source overwrites the snapshot
 *     so the most recent paid campaign gets credit for the conversion.
 *   - A later visit with no UTMs leaves the snapshot untouched.
 *   - Snapshot expires after 90 days.
 *
 * Safe for SSR (returns null/no-ops when window is absent) and for
 * environments where localStorage throws (Safari private mode, quota
 * exceeded, third-party cookie blocking — all silently no-op).
 */

const STORAGE_KEY = 'zta_attribution_v1';
const TTL_MS = 90 * 24 * 60 * 60 * 1000;

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'fbclid'];

// Length caps matched to the backend's FIELD_MAX_LENGTHS in
// backend/pipedrive/fieldMap.js. Defense in depth — the backend re-caps too.
const MAX_LENGTHS = {
  utm_source: 255,
  utm_medium: 255,
  utm_campaign: 255,
  utm_content: 255,
  fbclid: 255,
  landing_page: 2000,
  referrer: 2000,
};

function isBrowser() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function safeRead() {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch (_err) {
    return null;
  }
}

function safeWrite(value) {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch (_err) {
    // private browsing, quota exceeded, blocked storage — silently no-op
  }
}

function clipString(value, maxLength) {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  return trimmed.slice(0, maxLength);
}

function readQueryParams() {
  if (!isBrowser()) return {};
  try {
    const params = new URLSearchParams(window.location.search);
    const out = {};
    for (const key of UTM_KEYS) {
      const v = params.get(key);
      if (v) out[key] = clipString(v, MAX_LENGTHS[key]);
    }
    return out;
  } catch (_err) {
    return {};
  }
}

function currentLandingPage() {
  if (!isBrowser()) return '';
  try {
    return clipString(window.location.pathname + window.location.search, MAX_LENGTHS.landing_page);
  } catch (_err) {
    return '';
  }
}

function currentReferrer() {
  if (typeof document === 'undefined') return '';
  try {
    return clipString(document.referrer || '', MAX_LENGTHS.referrer);
  } catch (_err) {
    return '';
  }
}

function isExpired(snapshot) {
  if (!snapshot || typeof snapshot.firstTouch !== 'object' || !snapshot.firstTouch) return true;
  const capturedAt = Number(snapshot.firstTouch.capturedAt);
  if (!Number.isFinite(capturedAt)) return true;
  return Date.now() - capturedAt > TTL_MS;
}

/**
 * Idempotent capture. Call once per page load (or per route change in an SPA).
 * Updates the persisted snapshot in place; never blocks rendering.
 */
export function captureAttributionOnLanding() {
  if (!isBrowser()) return;

  const incoming = readQueryParams();
  const stored = safeRead() || {};
  const now = Date.now();

  // Drop expired snapshots so a stale first-touch can't override a fresh organic visit.
  if (stored.firstTouch && isExpired(stored)) {
    delete stored.firstTouch;
  }

  const hasIncomingUtmSource = Boolean(incoming.utm_source);
  const storedSource = stored.firstTouch?.utm_source || '';
  const isNewPaidSource = hasIncomingUtmSource && storedSource !== incoming.utm_source;

  if (isNewPaidSource) {
    // Refresh-on-new-paid-source: overwrite with the current campaign context.
    stored.firstTouch = {
      ...incoming,
      landing_page: currentLandingPage(),
      referrer: currentReferrer(),
      capturedAt: now,
    };
  } else if (!stored.firstTouch) {
    // First ever touch (even if organic) — capture landing + referrer.
    stored.firstTouch = {
      ...incoming,
      landing_page: currentLandingPage(),
      referrer: currentReferrer(),
      capturedAt: now,
    };
  }

  stored.lastSeenAt = now;
  safeWrite(stored);
}

/**
 * Returns the active first-touch snapshot in the exact shape the backend
 * expects, or null if no valid snapshot is stored. All seven keys are always
 * present (empty string for missing values) so the payload shape is stable.
 */
export function getAttribution() {
  const stored = safeRead();
  if (!stored || isExpired(stored)) return null;
  const t = stored.firstTouch;
  return {
    utm_source:   clipString(t.utm_source,   MAX_LENGTHS.utm_source),
    utm_medium:   clipString(t.utm_medium,   MAX_LENGTHS.utm_medium),
    utm_campaign: clipString(t.utm_campaign, MAX_LENGTHS.utm_campaign),
    utm_content:  clipString(t.utm_content,  MAX_LENGTHS.utm_content),
    fbclid:       clipString(t.fbclid,       MAX_LENGTHS.fbclid),
    landing_page: clipString(t.landing_page, MAX_LENGTHS.landing_page),
    referrer:     clipString(t.referrer,     MAX_LENGTHS.referrer),
  };
}

/**
 * Test/dev helper for clearing the persisted snapshot. Not used in production
 * code paths but exposed for QA scripts and the local smoke test.
 */
export function clearAttribution() {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (_err) {
    // no-op
  }
}
